/**
 * Centralized formatting utilities for dates, times, and numbers
 * These functions respect user preferences for display formatting
 */

/**
 * Format a date according to user preferences
 * @param {Date|string} date - Date to format
 * @param {string} format - 'DD/MM/YYYY', 'MM/DD/YYYY', or 'YYYY-MM-DD'
 * @returns {string} Formatted date
 */
export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  switch (format) {
    case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
    case 'DD/MM/YYYY':
    default: return `${day}/${month}/${year}`;
  }
};

/**
 * Format a date with month name (e.g., "15 Jan 2024" or "Jan 15, 2024")
 * @param {Date|string} date - Date to format
 * @param {string} format - 'DD/MM/YYYY', 'MM/DD/YYYY', or 'YYYY-MM-DD'
 * @returns {string} Formatted date with month name
 */
export const formatDateLong = (date, format = 'DD/MM/YYYY') => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const day = d.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[d.getMonth()];
  const year = d.getFullYear();

  switch (format) {
    case 'MM/DD/YYYY': return `${month} ${day}, ${year}`;
    case 'YYYY-MM-DD': return `${year} ${month} ${day}`;
    case 'DD/MM/YYYY':
    default: return `${day} ${month} ${year}`;
  }
};

/**
 * Format a time according to user preferences
 * @param {Date|string} date - Date/time to format
 * @param {string} format - '12h' or '24h'
 * @returns {string} Formatted time
 */
export const formatTime = (date, format = '12h') => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  if (format === '24h') {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

/**
 * Format a number according to user preferences
 * @param {number} value - Number to format
 * @param {string} locale - 'en-AU', 'en-US', 'de-DE', 'fr-FR'
 * @param {number} decimals - Decimal places (default: 2)
 * @returns {string} Formatted number
 */
export const formatNumber = (value, locale = 'en-AU', decimals = 2) => {
  if (value == null || isNaN(value)) return '';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

/**
 * Format currency according to user preferences
 * @param {number} value - Amount to format
 * @param {string} locale - 'en-AU', 'en-US', 'de-DE', 'fr-FR'
 * @param {string} currency - Currency code (default: 'AUD')
 * @returns {string} Formatted currency
 */
export const formatCurrency = (value, locale = 'en-AU', currency = 'AUD') => {
  if (value == null || isNaN(value)) return '';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(value);
};

/**
 * Format date with time according to user preferences
 * @param {Date|string} date - Date/time to format
 * @param {string} dateFormat - 'DD/MM/YYYY', 'MM/DD/YYYY', or 'YYYY-MM-DD'
 * @param {string} timeFormat - '12h' or '24h'
 * @returns {string} Formatted date and time
 */
export const formatDateTime = (date, dateFormat = 'DD/MM/YYYY', timeFormat = '12h') => {
  if (!date) return '';
  return `${formatDate(date, dateFormat)} ${formatTime(date, timeFormat)}`;
};

/**
 * Get the first day of week index (0 = Sunday, 1 = Monday)
 * @param {string} firstDay - 'sunday' or 'monday'
 * @returns {number} Day index
 */
export const getFirstDayOfWeekIndex = (firstDay = 'monday') => {
  return firstDay === 'sunday' ? 0 : 1;
};

/**
 * Get ordered days of week based on user preference
 * @param {string} firstDay - 'sunday' or 'monday'
 * @returns {string[]} Array of day names
 */
export const getOrderedDaysOfWeek = (firstDay = 'monday') => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  if (firstDay === 'monday') {
    return [...days.slice(1), days[0]]; // Monday first
  }
  return days; // Sunday first
};

/**
 * Get ordered short day names based on user preference
 * @param {string} firstDay - 'sunday' or 'monday'
 * @returns {string[]} Array of short day names
 */
export const getOrderedDaysShort = (firstDay = 'monday') => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  if (firstDay === 'monday') {
    return [...days.slice(1), days[0]]; // Monday first
  }
  return days; // Sunday first
};

/**
 * Format relative date (Today, Yesterday, or formatted date)
 * @param {Date|string} date - Date to format
 * @param {string} format - Date format preference
 * @returns {string} Relative or formatted date
 */
export const formatRelativeDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Compare only dates, not times
  const isToday = d.toDateString() === today.toDateString();
  const isYesterday = d.toDateString() === yesterday.toDateString();

  if (isToday) return 'Today';
  if (isYesterday) return 'Yesterday';
  return formatDate(date, format);
};

/**
 * Get today's date in a specific timezone as YYYY-MM-DD string
 * This is used for database queries where dates are stored as YYYY-MM-DD
 * @param {string} timezone - IANA timezone string (e.g., 'Australia/Sydney')
 * @returns {string} Today's date in YYYY-MM-DD format for the given timezone
 */
export const getTodayInTimezone = (timezone = 'Australia/Sydney') => {
  try {
    const now = new Date();
    // Format date in the target timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    // en-CA locale gives us YYYY-MM-DD format directly
    return formatter.format(now);
  } catch (error) {
    // Fallback to Australia/Sydney if timezone is invalid
    console.warn(`Invalid timezone "${timezone}", falling back to Australia/Sydney`);
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Australia/Sydney',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return formatter.format(now);
  }
};

/**
 * Get the first day of the current month in a specific timezone as YYYY-MM-DD string
 * @param {string} timezone - IANA timezone string (e.g., 'Australia/Sydney')
 * @returns {string} First day of month in YYYY-MM-DD format
 */
export const getFirstDayOfMonthInTimezone = (timezone = 'Australia/Sydney') => {
  const today = getTodayInTimezone(timezone);
  // Replace the day portion with '01'
  return today.substring(0, 8) + '01';
};

/**
 * Get the first day of the current year in a specific timezone as YYYY-MM-DD string
 * @param {string} timezone - IANA timezone string (e.g., 'Australia/Sydney')
 * @returns {string} First day of year in YYYY-MM-DD format
 */
export const getFirstDayOfYearInTimezone = (timezone = 'Australia/Sydney') => {
  const today = getTodayInTimezone(timezone);
  // Replace month and day with '01-01'
  return today.substring(0, 5) + '01-01';
};
